import cx from 'classnames';
import * as React from 'react';
import 'swiper/css';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide, type SwiperProps } from 'swiper/react';

import { Arrow } from './parts/Arrow';
import * as styles from './styles.css';

export interface ISwiperCarouselProps extends SwiperProps {
  className?: string;
  testId?: string;
  children: React.ReactElement[];
}

export const SwiperCarousel: React.FC<React.PropsWithChildren<ISwiperCarouselProps>> = props => {
  const { children, testId, className, modules = [], ...swiperOptions } = props;

  /**
   * Помещаются ли все слайды, знает только Swiper: число слайдов на экране может меняться
   * по breakpoints. До его инициализации, в том числе на сервере, судим по slidesPerView,
   * после – по состоянию Swiper и его событиям lock/unlock.
   */
  const [isLocked, setIsLocked] = React.useState(
    () =>
      typeof swiperOptions.slidesPerView === 'number' && React.Children.count(children) <= swiperOptions.slidesPerView,
  );

  /**
   * Стрелки рендерятся после карусели, поэтому на первом рендере элементов навигации ещё нет.
   * Держим их в состоянии: когда ref'ы заполнятся, Swiper получит новый объект navigation
   * и инициализирует модуль сам. Переинициализировать навигацию на других элементах Swiper
   * не умеет, поэтому стрелки не размонтируются, а прячутся, когда все слайды помещаются.
   */
  const [prevEl, setPrevEl] = React.useState<HTMLDivElement | null>(null);
  const [nextEl, setNextEl] = React.useState<HTMLDivElement | null>(null);

  return (
    <div className={cx(styles['wrapper'], `${className}_wrapper`)} data-testid={testId}>
      <div className={styles['container']}>
        <Swiper
          {...swiperOptions}
          modules={[Navigation, ...modules]}
          navigation={{
            prevEl,
            nextEl,
            disabledClass: `${className}-button-disabled`,
          }}
          onAfterInit={swiper => setIsLocked(swiper.isLocked)}
          onLock={() => setIsLocked(true)}
          onUnlock={() => setIsLocked(false)}
        >
          {React.Children.map(children, (child, index) => (
            <SwiperSlide key={index}>{child}</SwiperSlide>
          ))}
        </Swiper>
      </div>
      <Arrow direction={-1} isHidden={isLocked} ref={setPrevEl} />
      <Arrow direction={1} isHidden={isLocked} ref={setNextEl} />
    </div>
  );
};

SwiperCarousel.displayName = 'SwiperCarousel';
